import React, { useState, useEffect, useCallback, useRef } from "react";
import ThinkingOverlay from "@/components/ThinkingOverlay";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { assessmentApi } from "../../services/assessmentApi";
import { ChevronLeft, ChevronRight, Check, Save, MessageSquare, Lightbulb, RefreshCw, Sparkles } from "lucide-react";
import FileAttachments from "@/components/FileAttachments";
import DemoAutoCompleteBar from "@/components/DemoAutoCompleteBar";

const PILLAR_LABELS = {
  business_strategy: "Business & AI Strategy Alignment",
  process_readiness: "Process & Use-Case Readiness",
  technology_data: "Technology & Data Foundation",
  organizational_readiness: "Organizational Readiness & Change",
  governance_security: "Governance, Security & Responsible AI",
  measurement_value: "Measurement & Value Realization",
};

/* ─── Talking Points Panel ──────────────────────────────────────── */

const TalkingPoints = ({ text, onRegenerate, regenerating }) => {
  if (!text) return null;
  // Parse text into sections by uppercase labels if present
  const sections = [];
  const lines = text.split(/\n+/);
  let current = null;
  const LABEL_RE = /^[•*\-]?\s*([A-Z][A-Z &/]{3,40}):\s*(.*)$/;
  const QUESTION_RE = /^\s*[-•*?]?\s*(.+\?)\s*$/;
  const questions = [];
  for (const ln of lines) {
    const m = ln.match(LABEL_RE);
    if (m) {
      current = { label: m[1], body: [m[2]].filter(Boolean) };
      sections.push(current);
      continue;
    }
    const q = ln.match(QUESTION_RE);
    if (q && ln.trim().endsWith("?")) {
      questions.push(q[1]);
      continue;
    }
    if (current) current.body.push(ln);
    else {
      if (!sections.length) sections.push({ label: null, body: [] });
      sections[0].body.push(ln);
    }
  }

  return (
    <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden" data-testid="talking-points-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#e5e7eb]">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#fef3c7]">
            <Lightbulb className="h-4 w-4 text-[#d97706]" />
          </div>
          <span className="text-[14px] font-semibold text-[#111827]">Consultant Talking Points</span>
        </div>
        {onRegenerate && (
          <button
            onClick={onRegenerate}
            disabled={regenerating}
            className="text-[12px] font-medium text-[#1a56db] hover:underline disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 inline mr-1 ${regenerating ? "animate-spin" : ""}`} />
            Regenerate
          </button>
        )}
      </div>

      <div className="p-5 space-y-4">
        {sections.map((sec, idx) => (
          <div key={idx}>
            {sec.label && (
              <div className="text-[10px] font-bold tracking-[0.08em] text-[#9ca3af] uppercase mb-1.5">
                {sec.label}
              </div>
            )}
            <p className="text-[13px] text-[#4b5563] leading-[1.6] whitespace-pre-wrap">
              {sec.body.join("\n").trim()}
            </p>
          </div>
        ))}

        {questions.length > 0 && (
          <div className="rounded-lg bg-[#f9fafb] border border-[#e5e7eb] p-4 space-y-2">
            <div className="text-[10px] font-bold tracking-[0.08em] text-[#9ca3af] uppercase">
              Probing Questions
            </div>
            {questions.map((q, i) => (
              <div key={i} className="flex gap-2 text-[13px] text-[#4b5563] leading-[1.5]">
                <span className="text-[#1a56db] font-bold shrink-0">?</span>
                <span>{q}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Consultant Comments Box ───────────────────────────────────── */

const ConsultantComments = ({ sessionId, questionId, initialValue, session }) => {
  const [comment, setComment] = useState(initialValue || "");
  const [saving, setSaving] = useState(false);
  const saveTimeout = React.useRef(null);

  useEffect(() => { setComment(initialValue || ""); }, [initialValue]);

  const saveComment = useCallback(async (text) => {
    setSaving(true);
    try {
      await assessmentApi.saveConsultantComment(sessionId, questionId, text, session);
    } catch { /* silent */ }
    finally { setSaving(false); }
  }, [sessionId, questionId, session]);

  const handleChange = (e) => {
    const val = e.target.value;
    setComment(val);
    // Auto-save after 800ms debounce
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => saveComment(val), 800);
  };

  return (
    <div
      className="rounded-xl border p-4 mt-3 assessment-comments"
      style={{ background: "#FFFFFF", borderColor: "rgba(0,0,0,0.12)" }}
      data-testid="consultant-comments-box"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5" style={{ color: "#5C6070" }} />
          <span className="text-xs font-semibold" style={{ color: "#5C6070" }}>Consultant Comments</span>
        </div>
        {saving && <span className="text-[10px] flex items-center gap-1" style={{ color: "#9499A8" }}><Save className="h-2.5 w-2.5" /> Saving...</span>}
      </div>
      <textarea
        value={comment}
        onChange={handleChange}
        rows={5}
        className="w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none"
        style={{
          background: "#FFFFFF",
          color: "#0F1117",
          borderColor: "rgba(0,0,0,0.12)",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "#3B4FE4";
          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(59,79,228,0.10)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)";
          e.currentTarget.style.boxShadow = "none";
        }}
        placeholder="Enter notes from the customer discussion..."
        data-testid="consultant-comments-textarea"
      />
    </div>
  );
};

/* ─── Single Question Renderer ──────────────────────────────────── */

const QuestionRenderer = ({ question, value, onChange }) => {
  const { response_type, answer_options, question_text, help_text } = question;
  
  // Add "I Don't Know" as last option for all question types
  const optionsWithIDK = [...(answer_options || []), { label: "I Don't Know", value: "i_dont_know", score: 0 }];

  // For toggle-based selection, manage array values
  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
  const toggleOption = (optValue) => {
    if (optValue === "i_dont_know") {
      // Selecting IDK clears all others
      onChange(selectedValues.includes("i_dont_know") ? [] : ["i_dont_know"]);
      return;
    }
    // Remove IDK if selecting a real option
    const filtered = selectedValues.filter(v => v !== "i_dont_know");
    if (filtered.includes(optValue)) {
      onChange(filtered.filter(v => v !== optValue));
    } else {
      onChange([...filtered, optValue]);
    }
  };

  return (
    <div className="space-y-5" data-testid={`question-${question.id}`}>
      <div>
        <p className="text-[20px] text-[#111827] font-bold leading-[1.3]">{question_text}</p>
        {help_text && <p className="text-[14px] text-[#6b7280] mt-3 leading-[1.6]">{help_text}</p>}
      </div>

      {(response_type === "single_select" || response_type === "multi_select") && (
        <>
          <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-[#9ca3af] mb-3">
            {response_type === "multi_select" ? "Select all that apply" : "Select one answer"}
          </div>
          <div className="space-y-2.5">
            {optionsWithIDK.map((opt, idx) => {
              const isSelected = selectedValues.includes(opt.value);
              const isIDK = opt.value === "i_dont_know";
              const isMulti = response_type === "multi_select";
              return (
                <button
                  key={idx}
                  onClick={() => toggleOption(opt.value)}
                  className={`w-full text-left px-4 py-3.5 rounded-[10px] transition-all text-[14px] flex items-center gap-3 ${
                    isSelected
                      ? "border-2 border-[#1a56db] bg-white text-[#111827] shadow-[0_0_0_3px_rgba(26,86,219,0.08)]"
                      : isIDK
                        ? "border border-dashed border-[#d1d5db] bg-white text-[#9ca3af] italic hover:border-[#9ca3af] hover:bg-[#f9fafb]"
                        : "border border-[#e5e7eb] bg-white text-[#111827] hover:border-[#93c5fd] hover:bg-[#f8faff]"
                  }`}
                  data-testid={`option-${opt.value}`}
                >
                  {/* Circle indicator — radio for single, check-dot for multi */}
                  <span
                    className={`relative shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isSelected ? "border-[#1a56db] bg-white" : "border-[#d1d5db] bg-white"
                    }`}
                  >
                    {isSelected && (
                      isMulti ? (
                        <Check className="h-3 w-3 text-[#1a56db]" strokeWidth={4} />
                      ) : (
                        <span className="h-2.5 w-2.5 rounded-full bg-[#1a56db]" />
                      )
                    )}
                  </span>
                  <span className="leading-[1.4] flex-1">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {response_type === "numeric_scale" && (
        <div className="flex gap-3">
          {[0, 1, 2, 3, 4].map((n) => (
            <button
              key={n}
              onClick={() => onChange(String(n))}
              className={`flex-1 py-4 rounded-[10px] text-[15px] font-semibold transition-all ${
                value === String(n)
                  ? "border border-[#1a56db] bg-[#e8eefb] text-[#1a56db]"
                  : "border border-[#e5e7eb] bg-white text-[#4b5563] hover:border-[#93c5fd] hover:bg-[#f8faff]"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      )}

      {response_type === "short_text" && (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-12 rounded-[10px] border border-[#e5e7eb] bg-white text-[#111827] px-4 text-[14px] placeholder:text-[#9ca3af] focus:border-[#1a56db] focus:ring-2 focus:ring-[#1a56db]/20 outline-none"
          placeholder="Enter your response..."
        />
      )}

      {response_type === "long_text" && (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-32 rounded-[10px] border border-[#e5e7eb] bg-white text-[#111827] px-4 py-3 text-[14px] resize-none placeholder:text-[#9ca3af] focus:border-[#1a56db] focus:ring-2 focus:ring-[#1a56db]/20 outline-none"
          placeholder="Describe in detail..."
        />
      )}
    </div>
  );
};

/* ─── Assessment Taker ──────────────────────────────────────────── */

const AssessmentTaker = ({ session, sessionId, onComplete, onBack }) => {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [thinkingActive, setThinkingActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await assessmentApi.getSession(sessionId, session);
      setData(d);
      const restored = {};
      const restoredComments = {};
      for (const r of (d.responses || [])) {
        restored[r.question_id] = r.response_value;
        if (r.consultant_comments) restoredComments[r.question_id] = r.consultant_comments;
      }
      setAnswers(restored);
      setComments(restoredComments);
    } catch {
      toast.error("Failed to load assessment");
    } finally {
      setLoading(false);
    }
  }, [sessionId, session]);

  const [generatingTP, setGeneratingTP] = useState(false);
  const [regeneratingTP, setRegeneratingTP] = useState(false);

  useEffect(() => { load(); }, [load]);

  // Item 5 (May-2026): Track which questions have been "visited" so we can
  // surface a "skipped" indicator for questions the user navigated past
  // without answering. Hooks must be declared BEFORE any early-return.
  const [visited, setVisited] = useState(new Set([0]));
  useEffect(() => {
    setVisited((prev) => {
      const next = new Set(prev);
      next.add(currentIdx);
      return next;
    });
  }, [currentIdx]);

  if (loading || !data) return <div className="text-center text-foreground/50 py-12 text-lg">Loading assessment...</div>;

  const assessmentType = data.session?.assessment_type || data.assessment_type || "initial";
  const questions = data.questions || [];
  const template = data.template || {};
  const totalQ = questions.length;
  
  // Handle empty questions array
  if (totalQ === 0) {
    return (
      <div className="max-w-6xl mx-auto" data-testid="assessment-taker">
        <div className="mb-6">
          <button onClick={onBack} className="text-sm text-foreground/40 hover:text-foreground/70 mb-3 flex items-center gap-1 transition-colors" data-testid="back-from-assessment">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <h2 className="text-2xl font-bold text-foreground tracking-tight">{template.title || "Assessment"}</h2>
        </div>
        <div className="text-center text-foreground/50 py-12 text-lg">No questions found for this assessment.</div>
      </div>
    );
  }
  
  const currentQ = questions[currentIdx];
  const answeredCount = Object.keys(answers).length;
  const progress = totalQ > 0 ? Math.round((answeredCount / totalQ) * 100) : 0;

  const sections = [];
  const seenPillars = new Set();
  for (const q of questions) {
    const p = q.opportunity_flag ? "__opportunity__" : (q.pillar || "__other__");
    if (!seenPillars.has(p)) {
      seenPillars.add(p);
      sections.push({ key: p, label: q.opportunity_flag ? "AI Opportunity" : (PILLAR_LABELS[p] || p), startIdx: questions.indexOf(q) });
    }
  }

  const currentSection = sections.findLast((s) => s.startIdx <= currentIdx);

  const handleAnswer = async (value) => {
    setAnswers(prev => ({ ...prev, [currentQ.id]: value }));
    setSaving(true);
    try {
      await assessmentApi.saveResponse(sessionId, {
        question_id: currentQ.id,
        response_value: value,
      }, session);
    } catch {
      // Silent save
    } finally {
      setSaving(false);
    }
  };

  // Compute skipped indices (uses the `visited` state declared above).
  const skippedIndices = (data?.questions || []).reduce((acc, q, i) => {
    if (i === currentIdx) return acc;
    const answered = answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== "";
    if (visited.has(i) && !answered) acc.push(i);
    return acc;
  }, []);
  const firstSkipped = skippedIndices[0];

  const thinkingMessages = assessmentType === "free"
    ? ["Analyzing your responses...", "Identifying key improvement areas...", "Generating initial recommendations...", "Preparing your snapshot report..."]
    : ["Analyzing your detailed responses...", "Evaluating workflow opportunities...", "Generating recommended AI workflows...", "Preparing your implementation roadmap..."];

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const result = await assessmentApi.completeSession(sessionId, session);
      toast.success("Assessment completed!");
      setThinkingActive(true);
      setTimeout(() => {
        setThinkingActive(false);
        if (onComplete) onComplete(result);
      }, assessmentType === "free" ? 5000 : 5000);
    } catch {
      toast.error("Failed to complete assessment");
      setCompleting(false);
    }
  };

  const handleGenerateAllTP = async () => {
    setGeneratingTP(true);
    try {
      const result = await assessmentApi.generateTalkingPoints({ session_id: sessionId }, session);
      toast.success(`Generated talking points for ${result.updated} questions`);
      load(); // Reload to get updated talking points
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate talking points");
    } finally {
      setGeneratingTP(false);
    }
  };

  const handleRegenerateTP = async (questionId) => {
    setRegeneratingTP(true);
    try {
      const result = await assessmentApi.regenerateSingleTalkingPoint({ question_id: questionId }, session);
      // Update the question in-place
      const updatedQuestions = data.questions.map(q =>
        q.id === questionId ? { ...q, talking_points: result.talking_points } : q
      );
      setData({ ...data, questions: updatedQuestions });
      toast.success("Talking point updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to regenerate");
    } finally {
      setRegeneratingTP(false);
    }
  };

  const allRequired = questions.filter((q) => q.required);
  const allRequiredAnswered = allRequired.every((q) => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== "");
  const isLast = currentIdx === totalQ - 1;

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="assessment-taker">
      <ThinkingOverlay show={thinkingActive} messages={thinkingMessages} duration={assessmentType === "free" ? 5000 : 5000} />

      {/* Topbar */}
      <div className="flex items-center justify-between gap-4 pb-5 border-b border-[#e5e7eb]">
        <div className="flex items-center gap-4 min-w-0">
          <button
            onClick={onBack}
            className="text-[13px] font-medium text-[#1a56db] hover:bg-[#e8eefb] px-2 py-1 rounded transition-colors flex items-center gap-1"
            data-testid="back-from-assessment"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <div className="min-w-0">
            {data.prospect?.company_name && (
              <div className="text-[11px] font-bold tracking-[0.10em] uppercase text-[#C79A3B] mb-0.5" data-testid="assessment-for-company">
                For {data.prospect.company_name}
                {data.prospect.industry && (
                  <span className="text-[#94a3b8] font-semibold normal-case tracking-normal capitalize"> · {data.prospect.industry.replace(/_/g, " ")}</span>
                )}
              </div>
            )}
            <h2 className="text-[18px] font-bold text-[#111827] tracking-tight truncate" style={{ letterSpacing: "-0.02em" }}>
              {template.title || "Assessment"}
            </h2>
            <p className="text-[12px] text-[#9ca3af] mt-0.5 capitalize">
              {template.industry?.replace("_", " ")} · {template.type === "initial" ? "Free Initial" : "Paid Detailed"}
              {data.prospect?.contact_name && <span> · {data.prospect.contact_name}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            className="h-9 rounded-lg border-[#e5e7eb] bg-white text-[#111827] text-[12px] font-semibold hover:bg-[#f9fafb] px-4 shadow-none"
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            Save Draft
          </Button>
          <Button
            onClick={handleGenerateAllTP}
            disabled={generatingTP}
            className="h-9 rounded-lg bg-[#1a56db] text-white text-[12px] font-semibold hover:bg-[#143faa] px-4 shadow-none"
            data-testid="generate-all-tp-btn"
          >
            {generatingTP ? <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generating...</> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Generate AI Notes</>}
          </Button>
        </div>
      </div>

      {/* Progress Strip */}
      <div className="py-4 border-b border-[#e5e7eb]">
        <div className="flex items-center justify-between text-[12px] text-[#4b5563] mb-2">
          <span>Question <span className="font-bold text-[#111827]">{currentIdx + 1}</span> of <span className="font-bold text-[#111827]">{totalQ}</span></span>
          <span className="flex items-center gap-3">
            {skippedIndices.length > 0 && (
              <button
                type="button"
                onClick={() => setCurrentIdx(firstSkipped)}
                className="text-[12px] font-semibold text-[#b45309] hover:underline flex items-center gap-1"
                data-testid="skipped-jump-btn"
                title="Jump to the first skipped question"
              >
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#f59e0b" }} />
                {skippedIndices.length} skipped
              </button>
            )}
            <span><span className="font-bold text-[#111827]">{progress}%</span> complete</span>
          </span>
        </div>
        <div className="flex gap-1 h-[6px]" data-testid="progress-strip">
          {questions.map((q, i) => {
            const isAnswered = answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== "";
            const isCurrent = i === currentIdx;
            // Item 5: amber dot if visited but unanswered (skipped)
            const isSkipped = !isAnswered && !isCurrent && visited.has(i);
            return (
              <button
                key={i}
                onClick={() => setCurrentIdx(i)}
                className={`flex-1 rounded-full transition-all ${
                  isAnswered
                    ? "bg-[#1a56db]"
                    : isSkipped
                      ? "bg-[#f59e0b]"
                      : isCurrent
                        ? "bg-[#93c5fd]"
                        : "bg-[#e5e7eb]"
                }`}
                data-testid={`progress-step-${i}${isSkipped ? "-skipped" : ""}`}
                aria-label={`Question ${i + 1}${isSkipped ? " (skipped)" : ""}`}
              />
            );
          })}
        </div>
      </div>

      {/* Split layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-0 mt-6">
        {/* Left: Question panel */}
        <div className="lg:pr-8 min-w-0">
          {/* Collapsible About */}
          <details className="group mb-6 rounded-[10px] border border-[#e5e7eb] bg-[#f9fafb]" data-testid="assessment-about">
            <summary className="cursor-pointer list-none select-none px-4 py-3 text-[12px] font-semibold text-[#4b5563] flex items-center justify-between">
              <span className="flex items-center gap-1.5">ⓘ About this assessment</span>
              <span className="text-[#9ca3af] transition-transform group-open:rotate-180">▼</span>
            </summary>
            <div className="px-4 pb-4 text-[13px] text-[#4b5563] leading-[1.65]">
              {assessmentType === "initial"
                ? "This free AI Readiness Assessment evaluates your organization across business process maturity, AI and data governance preparedness, and technology infrastructure readiness. 589 Labs provides honest, realistic assessments — we will not recommend services that don't make sense for your situation."
                : "This In-Depth Assessment provides a comprehensive deep-dive into your AI readiness across all operational dimensions — business processes, data governance, and technology infrastructure. 589 Labs is committed to honest evaluation and will only recommend implementation where genuine ROI exists."}
            </div>
          </details>

          {/* Pillar badge */}
          {currentSection && (
            <div className="mb-5">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#e8eefb] px-3 py-1 text-[12px] font-semibold text-[#1a56db]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#1a56db]" />
                {currentSection.label}
              </span>
            </div>
          )}

          {/* Question */}
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-7">
            <QuestionRenderer
              question={currentQ}
              value={answers[currentQ.id]}
              onChange={handleAnswer}
            />

            {/* Supporting files per question */}
            <div className="mt-6 pt-5 border-t border-[#f3f4f6]">
              <FileAttachments
                session={session}
                context="assessment_response"
                contextId={`${sessionId}:${currentQ.id}`}
                label="Supporting Files"
                emptyHint="Optional — attach screenshots, exports, or documents to support this answer."
                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                dataTestidPrefix={`q-${currentQ.id}`}
              />
            </div>
          </div>

          {/* Item 5 (May-2026): warn when there are skipped questions before
              completing the assessment so the user can jump back to fill them. */}
          {skippedIndices.length > 0 && (
            <div
              className="mt-4 mb-1 rounded-xl border border-[#fde68a] bg-[#fffbeb] px-4 py-3 flex items-start gap-3"
              data-testid="skipped-warning-banner"
            >
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", marginTop: 6, flexShrink: 0 }} />
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-[#92400e]">
                  {skippedIndices.length} question{skippedIndices.length === 1 ? "" : "s"} skipped
                </div>
                <div className="text-[12px] text-[#9a6708] mt-0.5">
                  Click a skipped progress dot above (amber) to jump back, or use the button below.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCurrentIdx(firstSkipped)}
                className="text-[12px] font-bold text-white bg-[#b45309] hover:bg-[#92400e] rounded-md px-3 py-1.5 shrink-0"
                data-testid="review-skipped-btn"
              >
                Review skipped →
              </button>
            </div>
          )}

          {/* Demo Mode: admin-only auto-complete bar (always rendered when Demo Mode is ON) */}
          <DemoAutoCompleteBar
            session={session}
            assessmentType={assessmentType}
            prospect={data?.prospect || {}}
            questions={questions}
            onApplyComplete={async (responses, profileKey) => {
              const result = await assessmentApi.demoAutoComplete(sessionId, responses, profileKey, session);
              setCompleting(true);
              setThinkingActive(true);
              setTimeout(() => {
                setThinkingActive(false);
                if (onComplete) onComplete(result);
              }, 3500);
            }}
          />

          {/* Nav row */}
          <div className="flex items-center justify-between gap-4 mt-5">
            <Button
              onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
              disabled={currentIdx === 0}
              variant="outline"
              className="h-10 rounded-lg border border-[#e5e7eb] bg-white text-[#111827] hover:bg-[#f9fafb] text-[13px] font-semibold px-4 shadow-none disabled:opacity-40"
              data-testid="prev-question-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>

            <div className="text-[13px] text-[#9ca3af] flex items-center gap-2">
              {saving && <span className="flex items-center gap-1"><Save className="h-3 w-3" /> Saving...</span>}
              <span><span className="font-semibold text-[#111827]">{currentIdx + 1}</span> / {totalQ}</span>
            </div>

            {isLast ? (
              <Button
                onClick={handleComplete}
                disabled={!allRequiredAnswered || completing}
                className="h-10 rounded-lg bg-[#10b981] text-white hover:bg-[#059669] text-[13px] font-semibold px-4 shadow-none"
                data-testid="complete-assessment-btn"
              >
                {completing ? "Completing..." : "Complete Assessment"} <Check className="h-4 w-4 ml-2" strokeWidth={3} />
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentIdx(Math.min(totalQ - 1, currentIdx + 1))}
                className="h-10 rounded-lg bg-[#1a56db] text-white hover:bg-[#143faa] text-[13px] font-semibold px-4 shadow-none"
                data-testid="next-question-btn"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>

        {/* Right: Consultant panel */}
        <aside className="hidden lg:block border-l border-[#e5e7eb] pl-6">
          {currentQ.talking_points ? (
            <>
              <TalkingPoints
                text={currentQ.talking_points}
                onRegenerate={() => handleRegenerateTP(currentQ.id)}
                regenerating={regeneratingTP}
              />
              {/* Industry Sector Insight callout — only when a pillar is active */}
              {currentSection && (
                <div className="mt-4 rounded-lg border-l-[3px] border-[#1a56db] bg-[#e8eefb] p-4">
                  <div className="text-[10px] font-bold tracking-[0.08em] text-[#1a56db] uppercase mb-1.5">
                    {template.industry?.toUpperCase()?.replace("_", " ")} SECTOR INSIGHT
                  </div>
                  <p className="text-[12px] text-[#111827] leading-[1.55]">
                    Probe how this area connects to the operator's real day-to-day — their concrete answer will sharpen the recommended workflow.
                  </p>
                </div>
              )}
              {assessmentType === "detailed" && (
                <div className="mt-4">
                  <ConsultantComments
                    sessionId={sessionId}
                    questionId={currentQ.id}
                    initialValue={comments[currentQ.id]}
                    session={session}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="rounded-xl border border-[#e5e7eb] bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-7 w-7 rounded-md bg-[#fef3c7] flex items-center justify-center">
                  <Lightbulb className="h-4 w-4 text-[#d97706]" />
                </div>
                <span className="text-[14px] font-semibold text-[#111827]">Consultant Talking Points</span>
              </div>
              <p className="text-[13px] text-[#9ca3af] leading-[1.6] mb-3">
                Click "Generate AI Notes" above to create context-aware talking points based on the prospect's profile.
              </p>
              <button
                onClick={() => handleRegenerateTP(currentQ.id)}
                disabled={regeneratingTP}
                className="text-[12px] text-[#1a56db] hover:underline flex items-center gap-1 font-medium"
                data-testid="generate-single-tp-btn"
              >
                {regeneratingTP ? <><RefreshCw className="h-3 w-3 animate-spin" /> Generating...</> : <><Sparkles className="h-3 w-3" /> Generate for this question</>}
              </button>
              {assessmentType === "detailed" && (
                <div className="mt-4">
                  <ConsultantComments
                    sessionId={sessionId}
                    questionId={currentQ.id}
                    initialValue={comments[currentQ.id]}
                    session={session}
                  />
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      {/* Mobile talking points */}
      {currentQ.talking_points && (
        <div className="lg:hidden mt-6">
          <TalkingPoints text={currentQ.talking_points} />
        </div>
      )}
    </div>
  );
};

export default AssessmentTaker;
